import React from "react";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import {formatReportMoney} from "components/Elements/ReportFilterToggles";

export function formatReceivablesDelta(row) {
	const delta = parseFloat(row?.receivables_delta ?? 0);
	if (!Number.isFinite(delta) || Math.abs(delta) < 0.00001) {
		return "0.00";
	}
	const prefix = delta > 0 ? "+" : "";
	return prefix + delta.toFixed(2);
}

export function formatBalanceRemaining(row) {
	const val = row?.balance_remaining;
	if (val == null || val === "") {
		return "—";
	}
	return parseFloat(val).toFixed(2);
}

export function ReportTooltipMoneyField({label, tooltip, value}) {
	return (
		<Tooltip title={tooltip || ""}>
			<span>
				<TextField
					placeholder={"0.00"}
					label={label}
					margin={"dense"}
					variant="outlined"
					value={value}
					disabled={true}
					InputLabelProps={{shrink: true}}
				/>
			</span>
		</Tooltip>
	);
}

export const RECEIVABLES_TOOLTIPS = {
	openReceivablesSales:
		"Closing combined invoice and layaway receivables balance as of the report end date for the selected date range.",
	openReceivablesInvoice:
		"Closing invoice receivables balance as of the report end date for the selected date range.",
	openReceivablesLayaway:
		"Closing layaway receivables balance as of the report end date for the selected date range.",
	totalOpenReceivablesSales:
		"Sum of all open invoice and layaway balances right now, regardless of the report date filter.",
	totalOpenReceivablesInvoice:
		"Sum of all open invoice balances right now, regardless of the report date filter.",
	totalOpenReceivablesLayaway:
		"Sum of all open layaway balances right now, regardless of the report date filter.",
};

const RECEIVABLES_HEADER_TOOLTIPS = {
	sales: {
		open: RECEIVABLES_TOOLTIPS.openReceivablesSales,
		totalOpen: RECEIVABLES_TOOLTIPS.totalOpenReceivablesSales,
	},
	invoice: {
		open: RECEIVABLES_TOOLTIPS.openReceivablesInvoice,
		totalOpen: RECEIVABLES_TOOLTIPS.totalOpenReceivablesInvoice,
	},
	layaway: {
		open: RECEIVABLES_TOOLTIPS.openReceivablesLayaway,
		totalOpen: RECEIVABLES_TOOLTIPS.totalOpenReceivablesLayaway,
	},
};

export function ReportReceivablesHeaderFields({invoicingEnabled, totalsRow, variant = "sales"}) {
	if (!invoicingEnabled) {
		return (
			<TextField
				placeholder={"0.00"}
				label={'Total Invoice Amount'}
				margin={"dense"}
				variant="outlined"
				value={formatReportMoney(totalsRow?.total_invoice ?? 0)}
				disabled={true}
				InputLabelProps={{shrink: true}}
			/>
		);
	}

	const tooltips = RECEIVABLES_HEADER_TOOLTIPS[variant] || RECEIVABLES_HEADER_TOOLTIPS.sales;

	return (
		<span className="report-receivables-header-fields" style={{display: "inline-flex", gap: 5}}>
			<ReportTooltipMoneyField
				label={'Open Receivables'}
				tooltip={tooltips.open}
				value={formatReportMoney(totalsRow?.total_receivables ?? 0)}
			/>
			<ReportTooltipMoneyField
				label={'Total Open Receivables'}
				tooltip={tooltips.totalOpen}
				value={formatReportMoney(totalsRow?.total_open_receivables ?? 0)}
			/>
		</span>
	);
}

export function ReportRollForwardSummary({rollForward, label}) {
	if (!rollForward) {
		return null;
	}
	const rows = [
		{key: "opening", label: "Opening Balance"},
		{key: "originated", label: "Originated"},
		{key: "collected", label: "Collected"},
		{key: "adjustments", label: "Adjustments / Write-offs"},
		{key: "closing", label: "Closing Balance"},
	];
	return (
		<div className="report-roll-forward" style={{marginBottom: 12, padding: "8px 12px", border: "1px solid #e0e0e0", borderRadius: 4}}>
			<strong>{label} Roll-forward</strong>
			<div className="d-flex flex-wrap" style={{marginTop: 8, gap: 16}}>
				{rows.map((row) => (
					<div key={row.key}>
						<div style={{fontSize: 12, color: "#666"}}>{row.label}</div>
						<div>{formatReportMoney(rollForward[row.key])}</div>
					</div>
				))}
			</div>
		</div>
	);
}

export function sumReceivablesDeltaFromData(data) {
	if (!Array.isArray(data)) {
		return 0;
	}
	return data.reduce((sum, row) => sum + parseFloat(row?.receivables_delta ?? 0), 0);
}

export function isInvoicingEnabledForReport(totalsRow, props) {
	if (totalsRow && totalsRow.invoicing_enabled != null) {
		return !!totalsRow.invoicing_enabled;
	}
	return !!(props && props.invoicing_enabled);
}
